import { Upload } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { useConfigStore } from "../../store/configStore";
import { useEditorStore } from "../../store/editorStore";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import React from "react";
import { toast } from "@/components/ui/use-toast";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { buildOutfit } from "../../lib/outfit";
import { createSpineAvatarUpload } from "../../lib/createSpineAvatarUpload";
import { spineEditorSelectionHasDynamicSpine } from "../../lib/hasDynamicSpineSelection";
import { ExportAnimationProgressDialog } from "../export/components/export-animation/ExportAnimationProgressDialog";
import { ImageUploadProgressDialog } from "./ImageUploadProgressDialog";
import {
  DEFAULT_VAP_ENCODE_LIMITS,
  type VapEncodeLimits,
} from "../export/components/export-vap/vapConfig";
import { VapEncodeSettings } from "./VapEncodeSettings";
import type { SpineTemplate } from "../../types/spine-template";
import {
  getVapOriginalDimensions,
  getVapPreviewDimensions,
} from "../../utils/fullscreenType";

function resetUploadFormState() {
  return {
    name: "",
    desc: "",
    encodeLimits: { ...DEFAULT_VAP_ENCODE_LIMITS },
  };
}

export function UploadAvatar() {
  const { tid } = useParams<{ tid: string }>();
  const { data: template } = useSWR<SpineTemplate>(
    tid ? [`/spine-templates/${tid}`] : null,
  );
  const fullscreenType = template?.fullscreenType ?? 0;
  const originalDims = getVapOriginalDimensions(fullscreenType);
  const previewDims = getVapPreviewDimensions(fullscreenType);
  const { templateConfig, loadedRelatedTemplates, spineData } = useConfigStore();
  const {
    selectedSkins,
    selectedToningColors,
    currentAnimation,
    existingSpineVersion,
    spineApp,
    spineCanvas,
    previewMergedStaticStack,
  } = useEditorStore();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [encodeLimits, setEncodeLimits] = useState<VapEncodeLimits>({
    ...DEFAULT_VAP_ENCODE_LIMITS,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [exportProgressOpen, setExportProgressOpen] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [uploadProgressOpen, setUploadProgressOpen] = useState(false);

  const hasDynamic = useMemo(
    () =>
      spineEditorSelectionHasDynamicSpine(
        selectedSkins,
        templateConfig,
        spineData,
        loadedRelatedTemplates,
      ),
    [selectedSkins, templateConfig, spineData, loadedRelatedTemplates],
  );

  const handleSave = async () => {
    if (!tid) {
      toast({
        variant: "destructive",
        title: "保存失败",
        description: "缺少模板 ID",
      });
      return;
    }

    const drawData = buildOutfit(
      {
        config: templateConfig,
        id: tid,
        spineVersion: existingSpineVersion ?? undefined,
      },
      loadedRelatedTemplates,
      selectedSkins,
      selectedToningColors,
      currentAnimation,
    );
    if (!drawData) {
      toast({
        variant: "destructive",
        title: "保存失败",
        description: "无法获取当前搭配数据",
      });
      return;
    }

    setIsSaving(true);
    setExportProgressOpen(true);
    setExportProgress(0);

    try {
      const { mode } = await createSpineAvatarUpload({
        tid,
        drawDataJson: JSON.stringify(drawData),
        selectedSkins,
        templateConfig,
        spineData,
        loadedRelatedTemplates,
        spineApp,
        spineCanvas,
        previewMergedStaticStack,
        fullscreenType,
        encodeLimits,
        name,
        desc,
        onExportProgress: setExportProgress,
        onUploadStart: () => {
          setExportProgressOpen(false);
          setExportProgress(0);
          setUploadProgressOpen(true);
        },
      });

      setOpen(false);
      const reset = resetUploadFormState();
      setName(reset.name);
      setDesc(reset.desc);
      setEncodeLimits(reset.encodeLimits);
      toast({
        title: "保存成功",
        description:
          mode === "image"
            ? "Avatar 已创建（静态图），worker 将完成落库"
            : "Avatar 已创建，VAP 视频已上传，worker 将生成封面并完成落库",
        className: "bg-green-500 text-white",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "保存失败",
        description: error instanceof Error ? error.message : "未知错误",
      });
    } finally {
      setExportProgressOpen(false);
      setExportProgress(0);
      setUploadProgressOpen(false);
      setIsSaving(false);
    }
  };

  const handleDialogOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen && !isSaving) {
      const reset = resetUploadFormState();
      setName(reset.name);
      setDesc(reset.desc);
      setEncodeLimits(reset.encodeLimits);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        disabled={!templateConfig || selectedSkins.length === 0}
        variant="outline"
        size="default"
        className="gap-1"
      >
        <Upload className="h-4 w-4" />
        上传 Avatar
      </Button>

      <ExportAnimationProgressDialog
        open={exportProgressOpen}
        format="webp"
        progress={exportProgress}
        title={
          hasDynamic
            ? `正在生成 VAP 视频（${originalDims.outputWidth}×${originalDims.outputHeight} + ${previewDims.outputWidth}×${previewDims.outputHeight}）`
            : "正在合成静态图"
        }
      />

      <ImageUploadProgressDialog open={uploadProgressOpen} />

      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>上传 Avatar</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">名称</Label>
              <Input
                id="name"
                placeholder="请输入 Avatar 名称（可选）"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="desc">描述</Label>
              <Textarea
                id="desc"
                placeholder="请输入 Avatar 描述（可选）"
                value={desc}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setDesc(e.target.value)
                }
                disabled={isSaving}
                rows={4}
              />
            </div>

            {hasDynamic ? (
              <VapEncodeSettings
                value={encodeLimits}
                onChange={setEncodeLimits}
                fullscreenType={fullscreenType}
                disabled={isSaving}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                当前仅静态组件，将上传 webp 图片，不生成视频。
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleDialogOpenChange(false)}
              disabled={isSaving}
            >
              取消
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? "保存中..." : "保存"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
